'use client'
import { useRef, type FC } from 'react'
import { useParams } from 'next/navigation'
import styled from 'styled-components'
import { motion, useInView } from 'motion/react'
import { capitalizeFirstLetter, CONTACT_FORM_EXTERNAL_URL } from '@/utils'
import type { TypeLang } from '@/schema'
import type { TypeEventsSectionData } from '@/app/[lang]/page'
import { mq, presetsFluid, rgbAlpha, VARIANTS_FADE_FROM_BOTTOM_TO_TOP, VARIANTS_FADE_FROM_RIGHT_TO_LEFT, VARIANTS_STAGGER_CHILDREN, vw } from '@/styles'
import { useCart } from '@/hooks'
// import { MODALS } from '@/context'
import { ButtonCta, diagonalSvg } from './layout/AsidePreFooter'
import { Medium } from '../base'

type Props = {
  data: TypeEventsSectionData
  className?: string
}

const getTypeByLang:Record<TypeLang, { fair: string, event: string }> = {
  es: {
    fair: 'Feria',
    event: 'Evento'
  },
  en: {
    fair: 'Fair',
    event: 'Event'
  }
}

// The dates will be YYYY-MM-DD and we need to combine them into a single string
// Example: combineDates(2026-01-20, 2026-01-22, 'es') will output: '20 - 22 Enero 2026'
// Will receive first param as start date and second as end date, third the language
const combineDates = (startDateStr: string, endDateStr: string, lang: string) => {
  const startDate = new Date(startDateStr)
  const endDate = new Date(endDateStr)

  const optionsSameMonth: Intl.DateTimeFormatOptions = { day: 'numeric' }
  const optionsDifferentMonth: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' }
  const optionsDifferentYear: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' }
  let formattedDate = ''

  if (startDate.getFullYear() === endDate.getFullYear()) {
    if (startDate.getMonth() === endDate.getMonth()) {
      // Same month and year
      const startDay = startDate.toLocaleDateString(lang, optionsSameMonth)
      const endDay = endDate.toLocaleDateString(lang, optionsSameMonth)
      const monthYear = startDate.toLocaleDateString(lang, { month: 'long', year: 'numeric' })
      formattedDate = `${startDay} - ${endDay} ${capitalizeFirstLetter(monthYear)}`
    } else {
      // Different month but same year
      const startFormatted = startDate.toLocaleDateString(lang, optionsDifferentMonth)
      const endFormatted = endDate.toLocaleDateString(lang, optionsDifferentMonth)
      const year = startDate.getFullYear()
      formattedDate = `${startFormatted} - ${endFormatted} ${year}`
    }
  } else {
    // Different years
    const startFormatted = startDate.toLocaleDateString(lang, optionsDifferentYear)
    const endFormatted = endDate.toLocaleDateString(lang, optionsDifferentYear)
    formattedDate = `${startFormatted} - ${endFormatted}`
  }

  return formattedDate
}

const getItems = (data:Props['data']['list_items'], lang: TypeLang) => {
  return data.map((item, index) => (
    <motion.li key={index} variants={VARIANTS_FADE_FROM_RIGHT_TO_LEFT()}>
      <div className='link'>
        <article>
          <div className='medium-wrapper'>
            {diagonalSvg()}
            <Medium data={item.medium} />
            {/* <NextImage src={item.medium.src} alt={typeof item.medium.alt === 'string' ? item.medium.alt : item.medium.alt[lang]} width={item.medium.size.width} height={item.medium.size.height} /> */}
          </div>
          <hgroup>
            {getTypeByLang[lang]?.[item.type] && <sup>{getTypeByLang[lang][item.type]}</sup>}
            <h3>{item.name}</h3>
            <p>{item.description}</p>
          </hgroup>
          <span className='date'>{combineDates(item.start_date, item.end_date, lang)}</span>
        </article>
      </div>
    </motion.li>
  ))
}

const headingId = 'events'
export const SectionEvents:FC<Props> = ({ data, className = '' }) => {
  const { title, description, info, list_items, cta } = data
  const { lang } = useParams() as { lang: TypeLang }
  // const { updatePayload } = useModal(MODALS.AUTH_CONTACT)
  const refUl = useRef<HTMLUListElement>(null)
  const refHgroup = useRef<HTMLElement>(null)
  const isInViewHgroup = useInView(refHgroup, { once: true })
  const isInViewUl = useInView(refUl, { once: true })
  const { isCompactCartVisible } = useCart()

  const onClick = () => {
    // updatePayload({ open: true })
		window.location.href = CONTACT_FORM_EXTERNAL_URL
  }

  return (
    <Section aria-labelledby={headingId} $isCompactCartVisible={isCompactCartVisible} className={className}>
      <motion.hgroup ref={refHgroup} initial='initial' animate={isInViewHgroup ? 'animate' : 'initial'} variants={VARIANTS_FADE_FROM_BOTTOM_TO_TOP()}>
        <h2 id={headingId}>{title}</h2>
        <p className='desc'>{description}</p>
        <p className='info'>{info}</p>
        <ButtonCta className='button--cta' onClick={onClick}>{cta}</ButtonCta>
      </motion.hgroup>
      <motion.ul ref={refUl} initial='initial' animate={isInViewUl ? 'animate' : 'initial'} variants={VARIANTS_STAGGER_CHILDREN}>
        {getItems(list_items, lang)}
      </motion.ul>
    </Section>
  )
}

const Section = styled.section<{ $isCompactCartVisible?: boolean }>`
  border-top: 1px solid ${rgbAlpha('--color-text--light', .2)};
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;

  ${mq.greaterThan('tablet')} {
    flex-direction: row;
  }

  &.no-h2 {
    h2 {
      display: none;
    }
  }

  &.no-sup {
    sup {
      display: none;
    }
  }

  > hgroup,
  .link {
    padding: ${vw(20, 'mobile')} ${vw(10, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      padding: ${vw(20, 'tablet')} ${vw(10, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      padding: ${vw(30, 'desktop')};
    }
  }

  > hgroup {
    ${mq.greaterThan('tablet')} {
      display: flex;
      flex-direction: column;
      flex-wrap: nowrap;
      width: var(--viewport-width-custom-28);
      width: var(--viewport-width-custom-28--dynamic);
    }

    .desc {
      ${presetsFluid.font25_30()}
      font-weight: 400;
      margin-bottom: 100px;

      ${mq.greaterThan('tablet')} {
        flex-grow: 2;
        margin-bottom: 265px;
        position: sticky;
        top: 20px;
      }

      ${mq.greaterThan('desktop')} {
				${presetsFluid.font30_rel()}
        margin-bottom: 100px;
      }
    }

    .info {
      ${presetsFluid.font15()}
      margin-bottom: ${vw(30, 'mobile')};
      opacity: .7;

      ${mq.greaterThan('nexus7')} {
        margin-bottom: ${vw(30, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        margin-bottom: ${vw(30, 'desktop')};
        margin-top: auto;
      }

      ${mq.greaterThan('desktop')} {
        margin-bottom: 30px;
      }

			${mq.greaterThan('desktop')} {
				${presetsFluid.font15_rel()}
			}
    }

    .cta {
      margin-right: auto;
    }
  }

  h2 {
    ${presetsFluid.font18()}
    margin-bottom: ${vw(20, 'mobile')};
    opacity: .4;

    ${mq.greaterThan('nexus7')} {
      margin-bottom: ${vw(20, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-bottom: ${vw(20, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
			${presetsFluid.font18_rel()}
      margin-bottom: 20px;
    }
  }

  h2,
  h3,
  p {
    text-align: left;
  }

  ul {
    padding-bottom: 100px;

    ${mq.greaterThan('tablet')} {
      border-left: 1px solid ${rgbAlpha('--color-text--light', .2)};
      padding-bottom: 0;
      overflow-x: hidden;
      width: var(--viewport-width-custom-72);
      width: var(--viewport-width-custom-72--dynamic);
    }

    li {
      &:first-child {
        .link {
          ${mq.greaterThan('tablet')} {
            border-top: none;
          }
        }
      }
    }
  }

  .link {
    border-top: 1px solid ${rgbAlpha('--color-text--light', .2)};
  }

  article {
    display: flex;
    flex-direction: column;
    gap: ${vw(10, 'mobile')};
    position: relative;

    ${mq.greaterThan('nexus7')} {
      gap: ${vw(10, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      flex-direction: row;
      gap: ${vw(20, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      gap: 20px;
    }

    hgroup {
      margin-bottom: ${vw(10, 'mobile')};

      ${mq.greaterThan('nexus7')} {
        margin-bottom: ${vw(10, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        margin-bottom: 0;
        max-width: ${vw(428, 'desktop')};
      }

      p {
        ${presetsFluid.font15()}

				${mq.greaterThan('desktop')} {
					${presetsFluid.font15_rel()}
				}
      }
    }

    sup {
      display: none;
			${presetsFluid.font18()}

      ${mq.greaterThan('tablet')} {
        display: block;
        margin-bottom: 20px;
        opacity: .4;
      }

			${mq.greaterThan('desktop')} {
				${presetsFluid.font18_rel()}
			}
    }

    span {
      ${presetsFluid.font18()}
      margin-right: auto;
      opacity: .4;
      white-space: nowrap;

      ${mq.greaterThan('tablet')} {
        margin-left: auto;
        margin-right: 0;
        padding-left: ${vw(10, 'desktop')};
      }

			${mq.greaterThan('desktop')} {
				${presetsFluid.font18_rel()}
			}

			&.date {
				/* TEMPORARY HIDDEN (request from client) */
				display: none;
			}
    }

    svg {
      bottom: 0;
      position: absolute;
      right: 0;
    }
  }

  h3 {
    ${presetsFluid.font25_30()}
    font-weight: 400;
    margin-bottom: ${vw(10, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      margin-bottom: ${vw(10, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-bottom: ${vw(10, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
			${presetsFluid.font30_rel()}
      margin-bottom: 10px;
    }
  }

  .medium-wrapper {
    flex-shrink: 0;
    height: ${vw(160, 'mobile')};
    position: relative;
    width: 100%;

    ${mq.greaterThan('nexus7')} {
      height: ${vw(160, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      height: ${vw(225, 'desktop')};
      width: ${vw(292, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      height: 225px;
      width: 292px;
    }

    img,
    video {
      height: 100%;
      min-height: 100%;
      object-fit: cover;
      width: 100%;
      z-index: 2;

      ${mq.greaterThan('tablet')} {
        height: 100%;
        width: auto;
      }
    }
  }
`
